import { inject, Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { map, Observable, take } from 'rxjs';

import { GenerateRoomsResult, PlanItem } from '@efficiently/models';

import {
	GetPinDataResponse,
	PlanDetectionRunResult,
	PlanItemsRepository,
	UpdatePlanItemResponse,
} from '@efficiently/client-api-plans';
import { GraphQLClient } from '@efficiently/client-core-graphql';
import type { UpdatePlanItemMutationVariables } from '@efficiently/client-core-graphql-models';

import { CANCEL_PLAN_DETECTION } from '../queries/cancel-plan-detection';
import { CONFIRM_PLAN_DETECTION } from '../queries/confirm-plan-detection';
import { DETECT_PLAN_BOUNDARY } from '../queries/detect-plan-boundary';
import { DETECT_PLAN_INTERIOR } from '../queries/detect-plan-interior';
import { DRAG_PLAN_PAGE } from '../queries/drag-plan-page';
import { GET_PIN_DATA } from '../queries/get-pin-data';
import { UPDATE_PLAN_ITEM } from '../queries/update-plan-item';

@Injectable()
export class PlanItemsGqlRepository extends PlanItemsRepository {
	private readonly apollo = inject(Apollo);

	public override getPinData(projectId: string): Observable<GetPinDataResponse> {
		return this.apollo
			.watchQuery({
				query: GET_PIN_DATA,
				variables: {
					projectId,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.valueChanges.pipe(
				take(1),
				// TEMP-CODEGEN-FRONTIER(EFF-24658): result carries the app-wide `PlanItem` domain shape.
				map(({ data }) => data.getPinData as unknown as GetPinDataResponse),
			);
	}

	public override updatePlanItem(
		planItem: Partial<PlanItem> & Pick<PlanItem, '_id'>,
		roundLine: string,
		roundArea: string,
	): Observable<UpdatePlanItemResponse> {
		return this.apollo
			.mutate({
				mutation: UPDATE_PLAN_ITEM,
				// TEMP-CODEGEN-FRONTIER(EFF-24658): callers pass a domain `PlanItem` slice where the schema declares
				// `PlanItemInput`; the payload is unchanged.
				variables: {
					planItem,
					roundLine,
					roundArea,
				} as unknown as UpdatePlanItemMutationVariables,
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				take(1),
				// TEMP-CODEGEN-FRONTIER(EFF-24658): result carries the app-wide `PlanItem` / `Construction` / `Finish`
				// domain shapes.
				map(({ data }) => data.updatePlanItem as unknown as UpdatePlanItemResponse),
			);
	}

	public override dragPlanPage(pageIds: string[]): Observable<boolean> {
		return this.apollo
			.mutate({
				mutation: DRAG_PLAN_PAGE,
				variables: {
					pageIds,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				take(1),
				map(({ data }) => data.dragPlanPage),
			);
	}

	public override detectPlanBoundary(planItemId: string): Observable<PlanDetectionRunResult> {
		return this.apollo
			.mutate({
				mutation: DETECT_PLAN_BOUNDARY,
				variables: {
					planItemId,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				take(1),
				map(({ data }) => data.detectPlanBoundary),
			);
	}

	public override detectPlanInterior(planItemId: string): Observable<PlanDetectionRunResult> {
		return this.apollo
			.mutate({
				mutation: DETECT_PLAN_INTERIOR,
				variables: {
					planItemId,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				take(1),
				map(({ data }) => data.detectPlanInterior),
			);
	}

	public override cancelPlanDetection(planItemId: string, runId: string): Observable<boolean> {
		return this.apollo
			.mutate({
				mutation: CANCEL_PLAN_DETECTION,
				variables: {
					planItemId,
					runId,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				take(1),
				map(({ data }) => data.cancelPlanDetection),
			);
	}

	public override confirmPlanDetection(planItemId: string): Observable<GenerateRoomsResult> {
		return this.apollo
			.mutate({
				mutation: CONFIRM_PLAN_DETECTION,
				variables: {
					planItemId,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				take(1),
				map(({ data }) => data.confirmPlanDetection),
			);
	}
}
